import { useState } from 'react'
import {
  useAddClaim,
  useCustomers,
  useDeleteWarranty,
  useProjects,
  useResolveClaim,
  useSaveWarranty,
  useWarranties,
  useWarrantyClaims,
  useWarrantySummary,
  useWarrantyTypes
} from '../api/queries'
import type { Warranty, WarrantyInput } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { describeExpiry } from '../lib/format'
import { projectOptionLabel } from '../lib/projectLabel'
import { Card, EmptyState, ErrorBox, Loading } from '../components/ui'

function today() {
  const now = new Date()
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

function formatDate(value?: string | null) {
  if (!value) return '-'
  const [y, m, d] = value.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * "Udløber om 3 måneder", "Udløb for 12 dage siden".
 *
 * Enheden vokser med afstanden - en garanti løber typisk i år, og "om 1.247
 * dage" siger ingenting.
 */

/**
 * Garantier og reklamationer (beslutning 059).
 *
 * Siden svarer på det, kunden ringer om: er det dækket, hvor længe endnu, og
 * hvem hæfter. De der udløber først står øverst - det er dem, der haster, hvis
 * der skal reklameres.
 *
 * Alle må se; projektleder og opefter må oprette og rette.
 */
export function WarrantiesPage() {
  const { isProjectManager, isAdmin } = useAuth()
  const [search, setSearch] = useState('')
  const [expiringOnly, setExpiringOnly] = useState(false)
  const [includeExpired, setIncludeExpired] = useState(false)
  const [editing, setEditing] = useState<Warranty | null | undefined>(undefined)
  const [claiming, setClaiming] = useState<Warranty | null>(null)
  const [viewing, setViewing] = useState<Warranty | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const { data, isLoading, error } = useWarranties({ search, expiringOnly, includeExpired })
  const { data: summary } = useWarrantySummary()
  const remove = useDeleteWarranty()

  async function handleDelete(warranty: Warranty) {
    setActionError(null)
    if (!confirm(`Slet garantien på "${warranty.name}"?`)) return

    try {
      await remove.mutateAsync(warranty.id)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Garantien kunne ikke slettes.')
    }
  }

  return (
    <>
      <div className="cal-toolbar">
        <div className="cal-title">
          {summary ? `${summary.active} løbende` : `${data?.length ?? 0} garantier`}
        </div>

        <input
          placeholder="Søg i navn, leverandør, reference eller kunde ..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 280 }}
        />

        <label className="cal-person" style={{ cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={expiringOnly}
            onChange={(e) => setExpiringOnly(e.target.checked)}
          />
          Kun dem der udløber snart
        </label>

        <label className="cal-person" style={{ cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={includeExpired}
            onChange={(e) => setIncludeExpired(e.target.checked)}
          />
          Vis udløbne
        </label>

        {isProjectManager && (
          <button className="btn btn--primary btn--sm" onClick={() => setEditing(null)}>
            Opret garanti
          </button>
        )}
      </div>

      {summary && (summary.expiringSoon > 0 || summary.openClaims > 0) && (
        <div className="banner" style={{ marginBottom: 12, borderLeft: '4px solid #D97706' }}>
          {summary.expiringSoon > 0 && (
            <>
              <strong>
                {summary.expiringSoon}{' '}
                {summary.expiringSoon === 1 ? 'garanti udløber' : 'garantier udløber'} inden for{' '}
                {summary.warningDays} dage.
              </strong>{' '}
              Er der noget at reklamere over, skal det meldes inden - bagefter er det for sent.{' '}
            </>
          )}
          {summary.openClaims > 0 && (
            <>
              {summary.openClaims}{' '}
              {summary.openClaims === 1 ? 'reklamation er' : 'reklamationer er'} ikke afgjort endnu.
            </>
          )}
        </div>
      )}

      {actionError && <ErrorBox error={actionError} />}
      {error && <ErrorBox error={error} />}
      {isLoading && <Loading />}

      {data && data.length === 0 && (
        <EmptyState>
          {search || expiringOnly
            ? 'Ingen garantier passer på det, du søger efter.'
            : isProjectManager
              ? 'Der står ingen garantier endnu. Skriv dem ind, når et anlæg afleveres - så siger systemet selv til i god tid, før dækningen løber ud.'
              : 'Der står ingen garantier endnu.'}
        </EmptyState>
      )}

      {data && data.length > 0 && (
        <Card>
          <div className="stack" style={{ gap: 10 }}>
            {data.map((w) => (
              <div
                key={w.id}
                className="row"
                style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap', opacity: w.isActive ? 1 : 0.55 }}
              >
                <span
                  style={{
                    width: 6,
                    alignSelf: 'stretch',
                    minHeight: 44,
                    borderRadius: 3,
                    background: w.isExpired
                      ? '#9CA3AF'
                      : w.daysUntilExpiry <= (summary?.warningDays ?? 90)
                        ? '#D97706'
                        : w.color,
                    flexShrink: 0
                  }}
                />

                <div style={{ minWidth: 200, flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>
                    {w.name}
                    {!w.isActive && <span className="tiny muted"> · udgået</span>}
                  </div>
                  <div className="small muted">
                    {w.customerName}
                    {w.projectName && ` · ${w.projectName}`}
                    {w.deviceName && ` · ${w.deviceName}`}
                  </div>
                  {/* Hvem der haefter er hele pointen - derfor staar det, og ikke
                      bare typenavnet. */}
                  <div className="tiny" style={{ color: w.color }}>
                    {w.liable}
                    {w.supplier && ` · ${w.supplier}`}
                    {w.reference && ` · ${w.reference}`}
                  </div>
                </div>

                <div style={{ minWidth: 175 }}>
                  <div
                    className="small"
                    style={{
                      fontWeight: w.isExpired || w.daysUntilExpiry <= 90 ? 600 : 400,
                      color: w.isExpired ? '#6B7280' : w.daysUntilExpiry <= 90 ? '#B45309' : undefined
                    }}
                  >
                    {describeExpiry(w.daysUntilExpiry)}
                  </div>
                  <div className="tiny muted">
                    {formatDate(w.startsOn)} - {formatDate(w.expiresOn)}
                  </div>
                  {w.totalClaims > 0 && (
                    <div className="tiny" style={{ color: w.openClaims > 0 ? '#D97706' : undefined }}>
                      {w.totalClaims} {w.totalClaims === 1 ? 'reklamation' : 'reklamationer'}
                      {w.openClaims > 0 && `, ${w.openClaims} åben`}
                    </div>
                  )}
                </div>

                <div className="row" style={{ gap: 6 }}>
                  {w.totalClaims > 0 && (
                    <button className="btn btn--sm" onClick={() => setViewing(w)}>
                      Reklamationer
                    </button>
                  )}
                  <button className="btn btn--sm" onClick={() => setClaiming(w)}>
                    Meld fejl
                  </button>
                  {isProjectManager && (
                    <button className="btn btn--sm" onClick={() => setEditing(w)}>
                      Ret
                    </button>
                  )}
                  {isAdmin && (
                    <button className="btn btn--danger btn--sm" onClick={() => void handleDelete(w)}>
                      Slet
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {editing !== undefined && (
        <WarrantyDialog warranty={editing} onClose={() => setEditing(undefined)} />
      )}
      {claiming && <ClaimDialog warranty={claiming} onClose={() => setClaiming(null)} />}
      {viewing && <ClaimsDialog warranty={viewing} onClose={() => setViewing(null)} />}
    </>
  )
}

function Modal({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="modal__card">{children}</div>
    </div>
  )
}

function ClaimDialog({ warranty, onClose }: { warranty: Warranty; onClose: () => void }) {
  const add = useAddClaim()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [reportedOn, setReportedOn] = useState(today())
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await add.mutateAsync({
        warrantyId: warranty.id,
        title: title.trim(),
        description: description.trim() || null,
        reportedOn
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reklamationen kunne ikke gemmes.')
    }
  }

  return (
    <Modal onClose={onClose}>
      <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>Meld fejl på {warranty.name}</h2>
      <p className="small muted" style={{ marginTop: 0 }}>
        {warranty.customerName} · {warranty.liable}
      </p>

      {warranty.isExpired && (
        <p className="small" style={{ marginTop: 0, color: '#B45309' }}>
          <strong>Garantien er udløbet.</strong> Fejlen kan være opstået inden - meld den, og skriv
          den rigtige dato. Det er meldedatoen, kravet står og falder med.
        </p>
      )}

      <form onSubmit={(e) => void submit(e)}>
        <label className="field">
          <span className="field__label">Hvad er der galt</span>
          <input
            value={title}
            required
            placeholder="F.eks. Batteriet lader ikke op"
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">Uddybning (valgfri)</span>
          <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>

        <label className="field">
          <span className="field__label">Meldt af kunden</span>
          <input
            type="date"
            value={reportedOn}
            max={today()}
            onChange={(e) => setReportedOn(e.target.value)}
          />
        </label>

        <p className="tiny muted" style={{ marginTop: 0 }}>
          Datoen er det vigtigste felt: er fejlen meldt inden for garantien, gælder kravet - også
          selvom sagen først bliver afgjort bagefter.
        </p>

        {error && <ErrorBox error={error} />}

        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <button className="btn btn--primary" type="submit" disabled={add.isPending || !title.trim()}>
            {add.isPending ? 'Gemmer ...' : 'Meld fejlen'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Annullér
          </button>
        </div>
      </form>
    </Modal>
  )
}

function ClaimsDialog({ warranty, onClose }: { warranty: Warranty; onClose: () => void }) {
  const { data, isLoading } = useWarrantyClaims(warranty.id)
  const resolve = useResolveClaim()
  const { isProjectManager } = useAuth()
  const [resolving, setResolving] = useState<number | null>(null)
  const [status, setStatus] = useState('accepted')
  const [outcome, setOutcome] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function submit(claimId: number) {
    setError(null)
    try {
      await resolve.mutateAsync({
        warrantyId: warranty.id,
        claimId,
        status,
        outcome: outcome.trim() || null
      })
      setResolving(null)
      setOutcome('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reklamationen kunne ikke afgøres.')
    }
  }

  return (
    <Modal onClose={onClose}>
      <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>Reklamationer på {warranty.name}</h2>
      <p className="small muted" style={{ marginTop: 0 }}>
        {warranty.customerName} · {warranty.liable}
      </p>

      {isLoading && <Loading />}
      {error && <ErrorBox error={error} />}

      <div className="stack" style={{ gap: 12 }}>
        {(data ?? []).map((c) => (
          <div key={c.id}>
            <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
              <span style={{ fontWeight: 600 }}>{c.title}</span>
              <span className="tiny" style={{ color: c.color, fontWeight: 600 }}>
                {c.statusName}
              </span>
            </div>
            <div className="tiny muted">
              Meldt {formatDate(c.reportedOn)}
              {c.resolvedOn && ` · afgjort ${formatDate(c.resolvedOn)}`}
              {c.createdBy && ` · ${c.createdBy.displayName}`}
            </div>
            {c.description && <div className="small">{c.description}</div>}
            {c.outcome && (
              <div className="small" style={{ color: '#475569' }}>
                {c.outcome}
              </div>
            )}

            {isProjectManager && c.status === 'open' && (
              <div style={{ marginTop: 6 }}>
                {resolving === c.id ? (
                  <div className="stack" style={{ gap: 6 }}>
                    <select value={status} onChange={(e) => setStatus(e.target.value)}>
                      <option value="accepted">Anerkendt - den der hæfter dækker det</option>
                      <option value="rejected">Afvist</option>
                      <option value="closed">Afsluttet</option>
                    </select>
                    <input
                      value={outcome}
                      placeholder={
                        status === 'rejected'
                          ? 'Hvorfor blev den afvist? (påkrævet)'
                          : 'Hvad kom der ud af det?'
                      }
                      onChange={(e) => setOutcome(e.target.value)}
                    />
                    <div className="row" style={{ gap: 6 }}>
                      <button
                        className="btn btn--sm btn--primary"
                        onClick={() => void submit(c.id)}
                        disabled={resolve.isPending}
                      >
                        Gem
                      </button>
                      <button className="btn btn--sm" onClick={() => setResolving(null)}>
                        Annullér
                      </button>
                    </div>
                  </div>
                ) : (
                  <button className="btn btn--sm" onClick={() => setResolving(c.id)}>
                    Afgør
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="row" style={{ gap: 8, marginTop: 12 }}>
        <button className="btn" onClick={onClose}>
          Luk
        </button>
      </div>
    </Modal>
  )
}

function WarrantyDialog({ warranty, onClose }: { warranty: Warranty | null; onClose: () => void }) {
  const { data: customers } = useCustomers()
  const { data: projects } = useProjects({ pageSize: 200 })
  const { data: types } = useWarrantyTypes()
  const save = useSaveWarranty()

  const [form, setForm] = useState<WarrantyInput>({
    name: warranty?.name ?? '',
    warrantyType: warranty?.warrantyType ?? 'supplier',
    customerId: warranty?.customerId ?? 0,
    projectId: warranty?.projectId ?? null,
    supplier: warranty?.supplier ?? '',
    reference: warranty?.reference ?? '',
    startsOn: warranty?.startsOn ?? today(),
    months: warranty ? null : 24,
    expiresOn: warranty?.expiresOn ?? '',
    notes: warranty?.notes ?? '',
    isActive: warranty?.isActive ?? true
  })
  const [error, setError] = useState<string | null>(null)

  const selectedType = (types ?? []).find((t) => t.code === form.warrantyType)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await save.mutateAsync({
        id: warranty?.id,
        body: {
          ...form,
          supplier: form.supplier?.trim() || null,
          reference: form.reference?.trim() || null,
          expiresOn: form.expiresOn || null,
          notes: form.notes?.trim() || null
        }
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Garantien kunne ikke gemmes.')
    }
  }

  return (
    <Modal onClose={onClose}>
      <h2 style={{ margin: '0 0 14px', fontSize: 18 }}>
        {warranty ? 'Ret garanti' : 'Opret garanti'}
      </h2>

      <form onSubmit={(e) => void submit(e)}>
        <label className="field">
          <span className="field__label">Hvad dækker den</span>
          <input
            value={form.name}
            required
            placeholder="F.eks. Batteri, BESS-anlæg"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </label>

        <label className="field">
          <span className="field__label">Hvem hæfter</span>
          <select
            value={form.warrantyType}
            onChange={(e) => setForm({ ...form, warrantyType: e.target.value })}
          >
            {(types ?? []).map((t) => (
              <option key={t.code} value={t.code}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
        {selectedType && (
          <p className="tiny muted" style={{ marginTop: 0 }}>
            {selectedType.liable}.
          </p>
        )}

        <label className="field">
          <span className="field__label">Kunde</span>
          <select
            value={form.customerId}
            required
            onChange={(e) => setForm({ ...form, customerId: Number(e.target.value) })}
          >
            <option value={0} disabled>
              Vælg kunde ...
            </option>
            {(customers ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field__label">Sag (valgfri)</span>
          <select
            value={form.projectId ?? 0}
            onChange={(e) => setForm({ ...form, projectId: Number(e.target.value) || null })}
          >
            <option value={0}>Ingen bestemt sag</option>
            {(projects?.items ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {projectOptionLabel(p)}
              </option>
            ))}
          </select>
        </label>

        <div className="row" style={{ gap: 10 }}>
          <label className="field" style={{ flex: 1 }}>
            <span className="field__label">Leverandør</span>
            <input
              value={form.supplier ?? ''}
              placeholder="F.eks. Solar"
              onChange={(e) => setForm({ ...form, supplier: e.target.value })}
            />
          </label>
          <label className="field" style={{ flex: 1 }}>
            <span className="field__label">Serie- eller fakturanummer</span>
            <input
              value={form.reference ?? ''}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
            />
          </label>
        </div>
        <p className="tiny muted" style={{ marginTop: 0 }}>
          Uden nummeret kan kravet ikke gøres gældende - det er dét, leverandøren spørger om først.
        </p>

        <div className="row" style={{ gap: 10 }}>
          <label className="field" style={{ flex: 1 }}>
            <span className="field__label">Gælder fra</span>
            <input
              type="date"
              value={form.startsOn}
              required
              onChange={(e) => setForm({ ...form, startsOn: e.target.value })}
            />
          </label>
          <label className="field" style={{ flex: 1 }}>
            <span className="field__label">Måneders dækning</span>
            <input
              type="number"
              min={1}
              max={600}
              value={form.months ?? ''}
              placeholder="F.eks. 24"
              onChange={(e) =>
                setForm({ ...form, months: e.target.value ? Number(e.target.value) : null })
              }
            />
          </label>
        </div>

        <label className="field">
          <span className="field__label">Eller en bestemt udløbsdato</span>
          <input
            type="date"
            value={form.expiresOn ?? ''}
            onChange={(e) => setForm({ ...form, expiresOn: e.target.value })}
          />
        </label>
        <p className="tiny muted" style={{ marginTop: 0 }}>
          Sætter du en dato, vinder den over månederne. Ellers regnes den ud - to år fra 1. januar
          løber til og med 31. december året efter.
        </p>

        <label className="field">
          <span className="field__label">Note</span>
          <input
            value={form.notes ?? ''}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </label>

        <label className="cal-person" style={{ cursor: 'pointer', marginBottom: 10 }}>
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
          />
          Garantien gælder
        </label>

        {error && <ErrorBox error={error} />}

        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <button
            className="btn btn--primary"
            type="submit"
            disabled={save.isPending || !form.customerId}
          >
            {save.isPending ? 'Gemmer ...' : 'Gem'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Annullér
          </button>
        </div>
      </form>
    </Modal>
  )
}
